/** @format */
/**
 * eslint-disable no-shadow
 */

import { Inject, Injectable, OnDestroy } from '@angular/core';
import {
	AnalyticsPricingBuckets,
	BucketedDefiningAttribute,
	BundleData,
	Chain,
	DefiningAttributeProperty,
	ProductListSEO,
	ProductPrice,
	ProductsData,
	ProductSku,
	ProductSkuPrices,
	ProductStyle,
	SkuLevelAttributes,
	StyleDefiningAttribute
} from '@shared/data-access/interfaces';
import { Store as NGRXStore } from '@ngrx/store';
import {
	bundleProductDataInitialized,
	productStyleAndSkusInitialized,
	selectAllProductSkus,
	selectAllProductStyle,
	selectBopisAndEcommInventory,
	selectBundleData,
	selectBundleProductsData,
	selectPrimaryCategory,
	primaryCategoryFetched,
	productHasBothEmptyAndNonEmptyPriceChecked,
	singleSkuProductResolved
} from '@pdp/data-access/state';
import { take, takeUntil, takeWhile } from 'rxjs/operators';

import { chain } from '@core/chain';
import {
	CategoryService,
	CookieService,
	Logger,
	StoreService,
	UtilityService,
	WINDOW
} from '@shared/data-access/services';

import { ProductDetailsByUrlResponse } from '@pdp/data-access/interfaces';
import { SkuPriceCollectionName } from '@shared/data-access/enums';
import { Subject } from 'rxjs';

import {
	AttributeOption,
	attributeOptionsProcessed,
	stateInitAttributeSelected
} from '@pdp/attributes/data-access';
import { ProductSelectionInventoryService } from '../product-selection-inventory.service';

import { getProductPriceFromSku } from '@pdp/utils';
import { BucketedProductPrices, BucketedSkuLevelPrices } from './bucketed-product-prices.interface';
import { ColorPriceMap } from './color-price-map.interface';
import { selectUserStoreNumber } from '@state/data-access';

@Injectable({
	providedIn: 'root'
})
export class ProductSelectionService implements OnDestroy {
	/**
	 * @description
	 * Used to keep a history of sku color
	 */
	private colorBucketMap = {};

	private UNSUBSCRIBE$: Subject<void> = new Subject();

	constructor(
		private ngrxStore: NGRXStore,
		private categoryService: CategoryService,
		private productSelectionInventoryService: ProductSelectionInventoryService,
		private storeService: StoreService,
		private utilityService: UtilityService,
		private logger: Logger,
		@Inject(WINDOW) private window: Window,
		private cookieService: CookieService,
		// eslint-disable-next-line @typescript-eslint/no-shadow
		private chain: Chain
	) {}

	/**
	 * @description intiaiizes the following data to be set to state store on page load
	 * product ecode, product skus, primary cat, userStoreId, Ecomm and Bopis inventory at a Ecode level
	 * @Important Ordering is very important with these method please do not shift around
	 */
	initializeAppDependentDataToState(productData: ProductsData[]): void {
		const productStyle = productData[0].style;
		const productSkus = productData[0].skus;

		this.setStateData(productStyle, productSkus);
	}

	/**
	 * @description
	 * Set Bundle Data in State
	 */
	initializeBundleDependentDataToState(productData: ProductDetailsByUrlResponse) {
		const bundleData = productData.bundleData;
		const bundleProductsData = productData.productsData;

		bundleProductsData.forEach((product) => {
			if (product.style && product.skus) {
				this.setColorAttributesBucket(product.style, product.skus);
			}
		});

		this.ngrxStore.dispatch(bundleProductDataInitialized({ bundleData, bundleProductsData }));
	}

	/**
	 * @description
	 * Dispatch Action to Product Reducer to store Product Data in state
	 */
	setProductDataToState(
		productStyle: ProductStyle | Record<string, never>,
		productSkus: ProductSku[] = null
	) {
		this.ngrxStore.dispatch(productStyleAndSkusInitialized({ productStyle, productSkus }));
	}

	/**
	 * @description Set Ecomm, Store Inventory and update location
	 **/
	getLocationForInventory() {
		let bopisLocation;
		const skuIdList = this.generateSkuIdListArray();
		this.ngrxStore
			.select(selectUserStoreNumber)
			.pipe(takeUntil(this.UNSUBSCRIBE$))
			.subscribe((storeId) => {
				let newStore;
				// Check to ensure there is a valid location string
				if (typeof storeId !== 'undefined') {
					// Convert to a number
					newStore = this.utilityService.convertToNumber(storeId);
				} else {
					const storeString = this.cookieService.get('DCSG_NGX_CUST_STORE');
					newStore = this.utilityService.convertToNumber(
						this.setBopisLocationString(storeString)
					);
				}

				if (!newStore) {
					newStore = this.chain.bopisLocationDefault;
				}

				if (newStore !== bopisLocation) {
					bopisLocation = newStore;
					this.productSelectionInventoryService.setInventoryResultsToState(
						bopisLocation,
						skuIdList
					);
				}
			});
	}

	setBopisLocationString(storeString) {
		return this.storeService.findStoreData(storeString, 'storenumber');
	}

	/**
	 * @description Transforms the attribute data of the product to a desireable format
	 * that will be used to populate attribute options on the PDP UI.
	 * An action will be dispatched to update the store after this is completed.
	 **/
	setDisplayableProductAttributesData(
		productStyle: ProductStyle | Record<string, never>,
		productSkus: ProductSku[],
		colorPriceMap?: ColorPriceMap | 0,
		isBundle?: boolean
	) {
		const attributeOptions: AttributeOption[][] = [];
		if (productStyle.definingAttributes?.length > 0) {
			// product skus contain the prices, will need to iterate through this and get the prices
			productStyle.definingAttributes.forEach((defAttr, index: number) => {
				const attrByGroup: AttributeOption[] = [];
				for (const defAttrValue of defAttr.value.split('|')) {
					if (defAttr.name.toLowerCase() === 'color' && colorPriceMap) {
						// Add a price field for colors
						attrByGroup.push({
							name: defAttr.name,
							value: defAttrValue,
							prices: colorPriceMap[defAttrValue]?.prices
						});
					} else {
						attrByGroup.push({
							name: defAttr.name,
							value: defAttrValue
						});
					}
				}
				attributeOptions[index] = attrByGroup;
				// if only 1 option available to select for attribute Group call to service
				// and set in state for selected option
				if (attrByGroup.length === 1) {
					this.ngrxStore
						.select(selectBopisAndEcommInventory)
						.pipe(
							takeWhile(
								(result) =>
									result.ecommInventory === null || result.bopisInventory === null
							)
						)
						.subscribe((result) => {
							if (result) {
								this.ngrxStore.dispatch(
									stateInitAttributeSelected({
										allProductSkus: productSkus,
										selectedAttribute: attrByGroup[0]
									})
								);
							}
						});
				}
			});
			if (isBundle) {
				return attributeOptions;
			}
			this.ngrxStore.dispatch(attributeOptionsProcessed({ attributeOptions }));
		} else if (productSkus) {
			// Check for single sku products without attrbute options and add resolved sku logic to
			// state without needing UI clicking selections.
			this.ngrxStore
				.select(selectBopisAndEcommInventory)
				.pipe(take(3))
				.subscribe((result) => {
					if (result.ecommInventory && result.bopisInventory) {
						this.ngrxStore.dispatch(
							singleSkuProductResolved({ resolvedSku: productSkus[0] })
						);
					}
				});
		}
	}

	/**
	 * @description Generates prices for each color swatch of the product.
	 * The generated object will have keys that represent the name of the color and the values will be the corresponding prices.
	 **/
	generatePricesForColors(productSkus: ProductSku[]): ColorPriceMap | 0 {
		const pricesForColors = {};
		if (productSkus?.length > 0 && productSkus[0]?.definingAttributes) {
			productSkus.forEach((productSku) => {
				if (!productSku?.definingAttributes) {
					return;
				}
				const color = productSku?.definingAttributes.find(
					(defAttr) => defAttr.name.toLowerCase() === 'color'
				)?.value;
				const productSkuCalculatedPrice = getProductPriceFromSku(productSku.prices);

				this.accumulatePrices(pricesForColors, color, productSkuCalculatedPrice);
			});
		}

		return Object.keys(pricesForColors).length > 0 ? pricesForColors : 0;
	}

	/**
	 * @description get PRIMARY CATEGORY from descriptive Attributes
	 */
	getPrimaryCategory(productStyle: ProductStyle | Record<string, never>) {
		this.setPrimaryCategoryData(productStyle?.primaryCategory);
	}

	/**
	 * @description method to set Primary Category for product in State
	 */
	setPrimaryCategoryData(primaryCatFromProductSkus: string) {
		// only CategoryTwo if standardSEOCallEnabled is disabled
		let primaryCategory: ProductListSEO;

		this.categoryService
			.getCategoryNameByID$(primaryCatFromProductSkus, 0)
			.subscribe({
				next: (response) => {
					primaryCategory = response;
				},
				error: (error) => {
					// Throw the error
					throw new Error(error.message);
				}
			})
			.add(() => {
				this.ngrxStore.dispatch(primaryCategoryFetched({ primaryCategory }));
			});
	}

	/**
	 * @description method to retrieve Product Style data from the store
	 */
	getProductStyleData(): null | ProductStyle | Record<string, never> {
		let productStyleData$;
		this.ngrxStore
			.select(selectAllProductStyle)
			.pipe(take(1))
			.subscribe((productStyleData) => {
				productStyleData$ = productStyleData;
			});
		return productStyleData$;
	}

	/**
	 * @description method to retrieve Bundle data from the store
	 */
	getBundleData(): null | BundleData | Record<string, never> {
		let bundleData$;
		this.ngrxStore
			.select(selectBundleData)
			.pipe(take(1))
			.subscribe((bundleData) => {
				bundleData$ = bundleData;
			});
		return bundleData$;
	}

	/**
	 * @description method to retrieveBundle Products data from the store
	 */
	getBundleProductsData(): null | ProductsData[] {
		let selectBundleProductsData$;
		this.ngrxStore
			.select(selectBundleProductsData)
			.pipe(take(1))
			// eslint-disable-next-line @typescript-eslint/no-shadow
			.subscribe((selectBundleProductsData) => {
				selectBundleProductsData$ = selectBundleProductsData;
			});
		return selectBundleProductsData$;
	}

	/**
	 * @description method to retrieve Product Style data from the store
	 */
	getProductSkusData(): null | ProductSku[] {
		let productSkusData$;
		this.ngrxStore
			.select(selectAllProductSkus)
			.pipe(take(1))
			.subscribe((productSkusData) => {
				productSkusData$ = productSkusData;
			});
		return productSkusData$;
	}

	/**
	 * @description method to retrieve Primary Category data from the store
	 */
	getPrimaryCategoryData() {
		let primaryCategoryData$;
		this.ngrxStore
			.select(selectPrimaryCategory)
			.pipe(take(1))
			.subscribe((primaryCategoryData) => {
				primaryCategoryData$ = primaryCategoryData;
			});
		return primaryCategoryData$;
	}

	generateSkuIdListArray(): string[] {
		const skuIdList = [];
		this.ngrxStore
			.select(selectAllProductSkus)
			.pipe(take(1))
			.subscribe((skuData: ProductSku[]) => {
				skuData.forEach((sku) => {
					skuIdList.push(sku.partNumber);
				});
			});
		return skuIdList;
	}

	/**
	 * @description Dispatches an action representing if this product has both an empty price and a priced associated with it.
	 */
	setHasBothEmptyAndNonEmptyPrice(colorPriceMap: ColorPriceMap) {
		const productPricesArr = Object.values(colorPriceMap);
		// Checks if all colors have the same value for empty price, if they don't then this product has 'see price in cart' and a price tag.
		const hasBothEmptyAndNonEmptyPrice = !productPricesArr.every(
			(value) => value.prices.emptyPrice === productPricesArr[0].prices.emptyPrice
		);
		this.ngrxStore.dispatch(
			productHasBothEmptyAndNonEmptyPriceChecked({ hasBothEmptyAndNonEmptyPrice })
		);
	}

	/**
	 * @description
	 * Accepts two defining attribute properties
	 * Returns which defining attribute property should be placed in front of the other
	 */
	public sortBySequenceThenValue(a: DefiningAttributeProperty, b: DefiningAttributeProperty) {
		if (+a.sequence < +b.sequence) {
			return -1;
		} else if (+a.sequence > +b.sequence) {
			return 1;
		} else if (+a.sequence === +b.sequence) {
			if (a.value.toString() > b.value.toString()) {
				return 1;
			} else if (a.value.toString() < b.value.toString()) {
				return -1;
			} else {
				return 0;
			}
		}
	}

	isSkuPriceClearance(skuPrice: ProductSkuPrices): boolean {
		return skuPrice.offerPrice.toFixed(2).toString().includes('.97');
	}

	/**
	 * @description
	 * Accepts a price object and returns productDisplay Price
	 */
	getBucketProductDisplayPrice(price: BucketedProductPrices): ProductPrice {
		const productPrice: ProductPrice = {
			minOfferPrice: price.minOffer,
			maxOfferPrice: price.maxOffer,
			minListPrice: price.minList ? price.minList : '',
			maxListPrice: price.maxList ? price.maxList : '',
			minSavingPct: price.dealsPercentage ? price.dealsPercentage : '',
			maxSavingPct: price.dealsPercentage ? price.dealsPercentage : '',
			mapPriceIndicator: price.mapPriceIndicator,
			priceIndicator: price.priceIndicator,
			emptyPrice: price.mapPriceIndicator === 1
		};

		return productPrice;
	}

	setAnalyticsBuckets(product: ProductStyle, skus: ProductSku[]): AnalyticsPricingBuckets {
		/*
		 * SKU logic
		 */
		// Check to see if there are any SKUs to iterate
		if (typeof skus === 'undefined' || skus.length === 0) {
			return;
		}

		const analyticsBucket: AnalyticsPricingBuckets = {
			bucketCount: {
				RegularArray: [],
				MarkDownArray: [],
				Clearancearray: []
			}
		};

		analyticsBucket.bucketCount.RegularArray = [];
		analyticsBucket.bucketCount.MarkDownArray = [];
		analyticsBucket.bucketCount.Clearancearray = [];

		// Iterate through the SKUs
		for (let index = 0; index < skus.length; ++index) {
			const sku = skus[index];
			if (
				index === 0 &&
				sku.definingAttributes !== undefined &&
				product.definingAttributes !== undefined &&
				sku.definingAttributes.length !== product.definingAttributes.length
			) {
				this.logger.warn({
					SkuAttributeAlert: 'true',
					Chain: chain.chainIdentifierAbbr,
					'E-Code': product.partNumber,
					ProductTitle: product.name,
					Browser: this.window.navigator.userAgent
				});
			}

			if (this.hasColorAttribute && !product.disableSkuLevelPricing) {
				if (sku.prices.offerPrice === Number(product.prices.maxlistprice)) {
					analyticsBucket.bucketCount.RegularArray.push(`${sku.partNumber}`);
				} else if (this.isSkuPriceClearance(sku.prices)) {
					analyticsBucket.bucketCount.Clearancearray.push(`${sku.partNumber}`);
				} else {
					analyticsBucket.bucketCount.MarkDownArray.push(`${sku.partNumber}`);
				}
			}
		}

		return analyticsBucket;
	}

	private setStateData(
		productStyle: ProductStyle | Record<string, never>,
		productSkus: ProductSku[]
	) {
		// Set Color Attributes Bucket
		if (productStyle && productSkus) {
			this.setColorAttributesBucket(productStyle, productSkus);
		}

		this.setProductDataToState(productStyle, productSkus);

		if (productSkus) {
			this.getLocationForInventory();
		}

		const colorPriceMapData: ColorPriceMap | 0 = this.generatePricesForColors(productSkus);

		// call attribute state method to sort attribute logic for the attribute state data
		this.setDisplayableProductAttributesData(productStyle, productSkus, colorPriceMapData);
		if (colorPriceMapData) {
			this.setHasBothEmptyAndNonEmptyPrice(colorPriceMapData);
		}

		this.getPrimaryCategory(productStyle);
	}

	private setColorAttributesBucket(
		productStyle: ProductStyle | Record<string, never>,
		productSku: ProductSku[]
	): void {
		productStyle.disableSkuLevelPricing = false;
		productStyle.colorAttributeBuckets = [];

		// Check to see if the product is custom
		const isLaunch = productStyle.descriptiveAttributes.find(
			(attr) => attr.identifier === '5476'
		);

		if (isLaunch) {
			productStyle.disableSkuLevelPricing = true;
		}

		const hasColorAttribute = this.hasColorAttribute(productStyle.definingAttributes);

		const skuLevelPrices: BucketedSkuLevelPrices = {
			regPrice: null,
			clearancePrice: null,
			markdownPrice: null
		};

		const skuLevelAttributes: SkuLevelAttributes = {
			regularPriceAttributeList: null,
			clearanceAttributeList: null,
			markDownAttributeList: null
		};

		this.iterateThroughSkus(
			productStyle,
			productSku,
			hasColorAttribute,
			skuLevelPrices,
			skuLevelAttributes
		);

		this.setColorAttributeBuckets(skuLevelPrices, skuLevelAttributes, productStyle);

		// Disable sku level pricing if we don't have more than one color bucket
		productStyle.disableSkuLevelPricing =
			productStyle.disableSkuLevelPricing || productStyle.colorAttributeBuckets.length < 2;
	}

	private hasColorAttribute(defAttributes: StyleDefiningAttribute[]): boolean {
		let defIndex = 0;
		let hasColorAttribute = false;
		while (defIndex < defAttributes.length && !hasColorAttribute) {
			if (defAttributes[defIndex].name.toLowerCase() === 'color') {
				hasColorAttribute = true;
			}
			++defIndex;
		}

		return hasColorAttribute;
	}

	private iterateThroughSkus(
		product: ProductStyle | Record<string, never>,
		productSkuData: ProductSku[],
		hasColorAttribute: boolean,
		skuLevelPrices: BucketedSkuLevelPrices,
		skuLevelAttributes: SkuLevelAttributes
	): void {
		let gtgtenabled = true;
		// Iterate the SKUs
		for (const productSku of productSkuData) {
			// Sku level pricing logic
			if (hasColorAttribute) {
				this.splitColorsIntoBuckets(
					productSku,
					product,
					skuLevelPrices,
					skuLevelAttributes
				);
			}

			if (!productSku.isGuaranteedToGetThere) {
				gtgtenabled = false;
			}
		}

		product.gtgtenabled = gtgtenabled;
	}

	/**
	 * @description
	 * Accepts a sku, product, sku level prices, sku level attributes and whethcr the current product has a color attribute
	 * Creates sku level pricing and sku level attributes depending on the sku price
	 */
	private splitColorsIntoBuckets(
		sku: ProductSku,
		product: ProductStyle | Record<string, never>,
		skuLevelPrices: BucketedSkuLevelPrices,
		skuLevelAttributes: SkuLevelAttributes
	): void {
		// To do, check if we need disableSkuLevelPricing
		if (!product.disableSkuLevelPricing) {
			if (sku.prices.offerPrice === sku.prices.listPrice) {
				product.disableSkuLevelPricing = this.colorInMultipleBuckets(
					sku,
					SkuPriceCollectionName.REGULAR
				);
				skuLevelAttributes.regularPriceAttributeList = this.assignAttributesToBuckets(
					sku,
					skuLevelAttributes.regularPriceAttributeList
				);
				skuLevelPrices.regPrice = this.calculateBucketPrice(skuLevelPrices.regPrice, sku);
			} else if (this.isSkuPriceClearance(sku.prices)) {
				product.disableSkuLevelPricing = this.colorInMultipleBuckets(
					sku,
					SkuPriceCollectionName.CLEARANCE
				);
				skuLevelAttributes.clearanceAttributeList = this.assignAttributesToBuckets(
					sku,
					skuLevelAttributes.clearanceAttributeList
				);
				skuLevelPrices.clearancePrice = this.calculateBucketPrice(
					skuLevelPrices.clearancePrice,
					sku
				);
			} else {
				product.disableSkuLevelPricing = this.colorInMultipleBuckets(
					sku,
					SkuPriceCollectionName.MARKDOWN
				);
				skuLevelAttributes.markDownAttributeList = this.assignAttributesToBuckets(
					sku,
					skuLevelAttributes.markDownAttributeList
				);
				skuLevelPrices.markdownPrice = this.calculateBucketPrice(
					skuLevelPrices.markdownPrice,
					sku
				);
			}
		}
	}

	/**
	 * @description
	 * Accepts sku and color attribute bucket array name
	 * Check the global colorBucketMap object to determine if the sku color has already been seen
	 */
	private colorInMultipleBuckets(
		sku: ProductSku,
		skuPriceCollectionName: SkuPriceCollectionName
	): boolean {
		let skuColor;
		let colorInMultipleBuckets = false;
		// Find sku color
		if (Array.isArray(sku.definingAttributes)) {
			for (const definingAttribute of sku.definingAttributes) {
				if (definingAttribute.name.toLowerCase() === 'color') {
					skuColor = definingAttribute.value;
				}
			}
		}
		// If our color map has a key of the current sku color then see if is already
		if (
			this.colorBucketMap[skuColor] &&
			this.colorBucketMap[skuColor] !== skuPriceCollectionName
		) {
			colorInMultipleBuckets = true;
		} else {
			this.colorBucketMap[skuColor] = skuPriceCollectionName;
		}

		return colorInMultipleBuckets;
	}

	/**
	 * @description
	 * Accepts a sku and an attributeList
	 * Creates a attribute list
	 */
	private assignAttributesToBuckets(
		sku: ProductSku,
		attributeList: BucketedDefiningAttribute
	): BucketedDefiningAttribute {
		if (!Array.isArray(sku.definingAttributes)) {
			return attributeList;
		}

		for (const attribute of sku.definingAttributes) {
			// if there are no elements yet
			if (attribute.name.toLowerCase() === 'color') {
				if (attributeList === null || attributeList === undefined) {
					const listItem = {
						name: attribute.name,
						selected: false,
						property: [
							{
								name: attribute.name,
								value: attribute.value,
								available: true,
								swatch: attribute.value,
								sequence: attribute.seq
							}
						]
					};

					attributeList = listItem;
				} else if (
					attributeList.property.find((x) => x.value === attribute.value) === undefined
				) {
					const propertyArray = attributeList.property;

					propertyArray.push({
						name: attribute.name,
						value: attribute.value,
						available: true,
						swatch: attribute.value,
						sequence: attribute.seq
					});

					const listItem = {
						name: attribute.name,
						selected: false,
						property: propertyArray
					};

					attributeList = listItem;
				}
			}
		}

		return attributeList;
	}

	/**
	 * @description
	 * Accepts a Price and Sku and tranforms the data into a ProductPrice interface
	 */
	calculateBucketPrice(price: BucketedProductPrices, sku: ProductSku): BucketedProductPrices {
		const minList =
			price && price.minList < sku.prices.listPrice ? price.minList : sku.prices.listPrice;
		const maxList =
			price && price.maxList > sku.prices.listPrice ? price.maxList : sku.prices.listPrice;
		const minOffer =
			price && price.minOffer < sku.prices.offerPrice
				? price.minOffer
				: sku.prices.offerPrice;
		const maxOffer =
			price && price.maxOffer > sku.prices.offerPrice
				? price.maxOffer
				: sku.prices.offerPrice;
		const mapPriceIndicator =
			price?.mapPriceIndicator === 1 ? price.mapPriceIndicator : sku.prices.mapPriceIndicator;
		const priceIndicator =
			price?.priceIndicator === 1 ? price.priceIndicator : sku.prices.priceIndicator;
		const dealsPercentage = Math.floor(sku.prices.dealsPercentage) || 0;

		return {
			maxList,
			maxOffer,
			priceIndicator,
			minList,
			minOffer,
			mapPriceIndicator,
			dealsPercentage
		};
	}

	/**
	 * @description
	 * Accepts sku level prices, sku level attributes and a product
	 * Creates colorAttributeBuckets
	 */
	private setColorAttributeBuckets(
		skuLevelPrices: BucketedSkuLevelPrices,
		skuLevelAttributes: SkuLevelAttributes,
		product: any
	): void {
		if (skuLevelPrices.regPrice) {
			skuLevelAttributes.regularPriceAttributeList.property.sort((a, b) =>
				this.sortBySequenceThenValue(a, b)
			);
			const productPrice = this.getBucketProductDisplayPrice(skuLevelPrices.regPrice);
			product.colorAttributeBuckets.push({
				bucket: {
					defAttributes: skuLevelAttributes.regularPriceAttributeList
				},
				bucketColors: this.getbucketColors(skuLevelAttributes.regularPriceAttributeList),
				productPrice,
				name: SkuPriceCollectionName.REGULAR
			});
		}

		if (skuLevelPrices.markdownPrice) {
			skuLevelAttributes.markDownAttributeList.property.sort((a, b) =>
				this.sortBySequenceThenValue(a, b)
			);
			const productPrice = this.getBucketProductDisplayPrice(skuLevelPrices.markdownPrice);
			product.colorAttributeBuckets.push({
				bucket: {
					defAttributes: skuLevelAttributes.markDownAttributeList
				},
				bucketColors: this.getbucketColors(skuLevelAttributes.markDownAttributeList),
				productPrice,
				name: SkuPriceCollectionName.MARKDOWN
			});
		}

		if (skuLevelPrices.clearancePrice) {
			skuLevelAttributes.clearanceAttributeList.property.sort((a, b) =>
				this.sortBySequenceThenValue(a, b)
			);
			const productPrice = this.getBucketProductDisplayPrice(skuLevelPrices.clearancePrice);
			product.colorAttributeBuckets.push({
				bucket: {
					defAttributes: skuLevelAttributes.clearanceAttributeList
				},
				bucketColors: this.getbucketColors(skuLevelAttributes.clearanceAttributeList),
				productPrice,
				name: SkuPriceCollectionName.CLEARANCE
			});
		}
	}

	/**
	 * @description
	 * Creates an array of Bucket Colors
	 *
	 */
	private getbucketColors(attributeList: BucketedDefiningAttribute): string[] {
		const bucketColors = [];
		attributeList.property.forEach((attr) => {
			bucketColors.push(attr.value);
		});
		return bucketColors;
	}

	private accumulatePrices(
		attributeSubsetPrices: object,
		key: string,
		productSkuCalculatedPrice: ProductPrice
	) {
		if (attributeSubsetPrices[key]) {
			// has price range case
			if (
				attributeSubsetPrices[key].prices?.minOfferPrice !==
				productSkuCalculatedPrice?.minOfferPrice
			) {
				attributeSubsetPrices[key].prices.hasPriceRange = true;
			}

			if (productSkuCalculatedPrice?.emptyPrice) {
				attributeSubsetPrices[key].prices.emptyPrice = true;
			}

			// Checking if this skus offer price is lower then what we currently have stored for the color
			// if so set lower bound of price for this color as this skus offer price
			if (
				productSkuCalculatedPrice?.minOfferPrice <
				attributeSubsetPrices[key].prices?.minOfferPrice
			) {
				attributeSubsetPrices[key].prices.minOfferPrice =
					productSkuCalculatedPrice.minOfferPrice;
			}

			// Checking if this skus list price is lower then what we currently have stored for the color
			// if so set lower bound of price for this color as this skus list price
			if (
				productSkuCalculatedPrice?.minListPrice <
				attributeSubsetPrices[key].prices?.minListPrice
			) {
				attributeSubsetPrices[key].prices.minListPrice =
					productSkuCalculatedPrice.minListPrice;
			}

			// Checking if this skus offer price is higher then what we currently have stored for the color
			// if so set upper bound of price for this color as this skus offer price
			if (
				productSkuCalculatedPrice?.minOfferPrice >
				attributeSubsetPrices[key].prices?.maxOfferPrice
			) {
				attributeSubsetPrices[key].prices.maxOfferPrice =
					productSkuCalculatedPrice.minOfferPrice;
			}

			// Checking if this skus list price is higher then what we currently have stored for the color
			// if so set upper bound of price for this color as this skus list price
			if (
				productSkuCalculatedPrice?.minListPrice >
				attributeSubsetPrices[key].prices?.maxListPrice
			) {
				attributeSubsetPrices[key].prices.maxListPrice =
					productSkuCalculatedPrice.minListPrice;
			}
		} else {
			attributeSubsetPrices[key] = {};
			attributeSubsetPrices[key].prices = productSkuCalculatedPrice;
		}
	}

	ngOnDestroy(): void {
		this.UNSUBSCRIBE$.next();
		this.UNSUBSCRIBE$.complete();
	}
}
